import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import EditIcon from '@mui/icons-material/Edit';

import { formatValue } from '@ts-core/utils/dataTableUtils';

import Typography from '@ts-core/components/Typography';
import BackButton from '@ts-core/components/BackButton';

const useStyles = makeStyles((theme) => ({
  editIcon: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}));

const AssignmentDetailsHeader = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { courseId } = useParams();

  const { onEditAssignment, onGoToTab, assignment, tabs, activeTab, loading } =
    props;

  const { stats } = assignment || {};

  const handleEditAssignment = useCallback(() => {
    onEditAssignment(assignment);
  }, [onEditAssignment, assignment]);

  const handleChangeTab = useCallback(
    (event, newValue) => {
      onGoToTab(newValue);
    },
    [onGoToTab]
  );

  const handleGoBack = useCallback(() => {
    history.push(`/${courseId}/assignments`);
  }, [history, courseId]);

  return (
    <>
      <BackButton
        onGoBack={handleGoBack}
        title={assignment?.name}
        subtitle="Entregas"
      />
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        marginBottom={2}
      >
        <ButtonBase
          className={classes.assignmentNameButton}
          onClick={handleEditAssignment}
          disabled={loading}
        >
          <Typography
            variant="h4"
            display="inline"
            loading={loading}
            skeletonWidth={180}
            skeletonHeight={32}
          >
            {assignment?.name}
          </Typography>
          {!loading && <EditIcon className={classes.editIcon} />}
        </ButtonBase>
        <Typography variant="subtitle1" loading={loading} skeletonWidth={60}>
          {moment(assignment?.expirationDate).format('DD/MM/YYYY HH:mm')}
        </Typography>
        <Box width="100%" justifyContent="center" display="flex">
          <Box
            display="flex"
            flexDirection="row"
            p={2}
            height={120}
            width={280}
          >
            <Box
              height="100%"
              width="50%"
              justifyContent="center"
              alignItems="center"
              display="flex"
              flexDirection="column"
              borderRight={1}
            >
              <Box
                flexGrow={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="h3"
                  component="p"
                  align="center"
                  loading={loading}
                  skeletonWidth={40}
                >
                  {stats?.answersCount}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                gutterBottom
                align="center"
                loading={loading}
                skeletonWidth={80}
              >
                <FormattedMessage id="assignment.answers" />
              </Typography>
            </Box>
            <Box
              height="100%"
              width="50%"
              alignItems="center"
              justifyContent="center"
              display="flex"
              flexDirection="column"
            >
              <Box
                flexGrow={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="h3"
                  component="p"
                  align="center"
                  loading={loading}
                  skeletonWidth={40}
                >
                  {formatValue(stats?.averageScore, stats?.maxScore)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                gutterBottom
                align="center"
                loading={loading}
                skeletonWidth={80}
              >
                <FormattedMessage id="assignment.averageScore" />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box borderBottom={1} borderTop={1}>
        {activeTab && (
          <Tabs value={activeTab?.slug} centered onChange={handleChangeTab}>
            {tabs.map((tab) => (
              <Tab key={tab.slug} value={tab.slug} label={tab.label} />
            ))}
          </Tabs>
        )}
      </Box>
    </>
  );
};

AssignmentDetailsHeader.propTypes = {
  assignment: PropTypes.object,
  loading: PropTypes.bool,
  tabs: PropTypes.array,
  activeTab: PropTypes.object,

  onGoToTab: PropTypes.func,
  onEditAssignment: PropTypes.func,
};

AssignmentDetailsHeader.defaultProps = {
  assignment: {},
  loading: false,
  activeTab: {},
  tabs: [],

  onGoToTab() {},
  onEditAssignment() {},
};

export default AssignmentDetailsHeader;
